import { useState } from 'react';
import { FaCode, FaRobot, FaCloud, FaTimes, FaCheck, FaArrowRight, FaLightbulb, FaServer, FaCogs, FaPlay } from 'react-icons/fa';
import styles from './Services.module.css';

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [activeWorkflow, setActiveWorkflow] = useState(0);

  const services = [
    {
      icon: <FaCode />,
      title: "Desarrollo Web",
      description: "Creamos sitios web modernos y responsivos que destacan tu marca.",
      features: [
        "Diseño UI/UX personalizado",
        "Desarrollo Frontend y Backend",
        "Optimización SEO",
        "Integración de CMS",
        "Comercio electrónico",
        "Aplicaciones web progresivas (PWA)"
      ],
      technologies: ["React", "Node.js", "Next.js", "WordPress", "MongoDB", "AWS"],
      process: [
        "Análisis de requerimientos",
        "Diseño de interfaz",
        "Desarrollo",
        "Testing",
        "Despliegue",
        "Mantenimiento"
      ]
    },
    {
      icon: <FaServer />,
      title: "Infraestructura",
      description: "Configuramos y gestionamos tu infraestructura en la nube de forma eficiente y segura.",
      features: [
        "Configuración de servidores",
        "Infraestructura como código (IaC)",
        "Arquitectura escalable",
        "Optimización de rendimiento",
        "Seguridad y cumplimiento",
        "Respaldo y recuperación"
      ],
      technologies: ["AWS", "Azure", "Google Cloud", "Terraform", "Ansible", "Docker"],
      process: [
        "Evaluación de necesidades",
        "Diseño de arquitectura",
        "Implementación",
        "Configuración",
        "Pruebas de carga",
        "Monitoreo continuo"
      ]
    },
    {
      icon: <FaRobot />,
      title: "Automatizaciones",
      description: "Optimizamos tus procesos con soluciones automatizadas.",
      features: [
        "Automatización de procesos",
        "Integración de sistemas",
        "Bots personalizados",
        "Flujos de trabajo",
        "RPA (Robotic Process Automation)",
        "Machine Learning"
      ],
      technologies: ["Python", "UiPath", "Power Automate", "TensorFlow", "Azure", "Docker"],
      process: [
        "Análisis de procesos",
        "Diseño de solución",
        "Implementación",
        "Pruebas",
        "Capacitación",
        "Monitoreo"
      ]
    },
    {
      icon: <FaCloud />,
      title: "DevOps",
      description: "Implementamos prácticas DevOps para mejorar tu desarrollo.",
      features: [
        "CI/CD",
        "Infraestructura como código",
        "Monitoreo continuo",
        "Gestión de contenedores",
        "Seguridad DevSecOps",
        "Automatización de pruebas"
      ],
      technologies: ["Jenkins", "Kubernetes", "Terraform", "AWS", "Docker", "Prometheus"],
      process: [
        "Evaluación",
        "Planificación",
        "Implementación",
        "Automatización",
        "Monitoreo",
        "Optimización"
      ]
    }
  ];

  // Flujo de trabajo general que se mostrará dinámicamente
  const workflowSteps = [
    {
      title: "Análisis y Planificación",
      description: "Evaluamos tus necesidades y diseñamos la estrategia perfecta",
      icon: <FaLightbulb />,
      details: "Realizamos un análisis profundo de tus requerimientos, objetivos de negocio y recursos disponibles para crear un plan de acción detallado."
    },
    {
      title: "Diseño y Arquitectura",
      description: "Creamos la estructura y diseño de tu solución",
      icon: <FaCogs />,
      details: "Desarrollamos la arquitectura técnica, diseños de interfaz y definimos las tecnologías más adecuadas para tu proyecto."
    },
    {
      title: "Desarrollo e Implementación",
      description: "Construimos tu solución con las mejores prácticas",
      icon: <FaCode />,
      details: "Nuestro equipo desarrolla tu proyecto siguiendo metodologías ágiles, asegurando calidad y eficiencia en cada etapa."
    },
    {
      title: "Testing y Optimización",
      description: "Probamos y optimizamos cada detalle",
      icon: <FaPlay />,
      details: "Realizamos pruebas exhaustivas, optimizamos el rendimiento y garantizamos que todo funcione perfectamente."
    },
    {
      title: "Despliegue y Lanzamiento",
      description: "Ponemos tu solución en producción",
      icon: <FaCloud />,
      details: "Desplegamos tu proyecto en el entorno de producción con todas las medidas de seguridad y monitoreo necesarias."
    },
    {
      title: "Soporte y Mantenimiento",
      description: "Te acompañamos en el crecimiento continuo",
      icon: <FaServer />,
      details: "Brindamos soporte técnico continuo, actualizaciones y mejoras para asegurar el éxito a largo plazo de tu proyecto."
    }
  ];

  return (
    <>
      <section className={styles.servicesHero}>
        <div className={styles.heroContent}>
          <div className={styles.badge}>
            <FaLightbulb className={styles.badgeIcon} />
            <span>Nuestros Servicios</span>
          </div>
          <h1>Soluciones Tecnológicas <span>Integrales</span></h1>
          <p>
            Ofrecemos un conjunto completo de servicios digitales diseñados para transformar 
            tu visión en realidad. Desde el desarrollo web hasta la automatización de procesos, 
            nuestro equipo está preparado para impulsar tu éxito digital.
          </p>
        </div>
      </section>

      <section className={styles.servicesSection}>
        <div className={styles.servicesContainer}>
          <div className={styles.sectionHeader}>
            <h2>Explora Nuestros Servicios</h2>
            <p>Selecciona el servicio que mejor se adapte a tus necesidades</p>
          </div>

          <div className={styles.servicesGrid}>
            {services.map((service, index) => (
              <div key={index} className={styles.serviceCard} onClick={() => setSelectedService(service)}>
                <div className={styles.iconContainer}>
                  {service.icon}
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <button className={styles.learnMore}>
                  Ver más <FaArrowRight />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nueva sección de flujo de trabajo */}
      <section className={styles.workflowSection}>
        <div className={styles.workflowContainer}>
          <div className={styles.workflowHeader}>
            <div className={styles.badge}>
              <FaCogs className={styles.badgeIcon} />
              <span>Nuestro Proceso</span>
            </div>
            <h2>Cómo Trabajamos <span>Contigo</span></h2>
            <p>
              Seguimos un proceso estructurado y transparente que garantiza 
              resultados excepcionales en cada proyecto.
            </p>
          </div>

          <div className={styles.workflowGrid}>
            {workflowSteps.map((step, index) => (
              <div 
                key={index} 
                className={`${styles.workflowCard} ${activeWorkflow === index ? styles.active : ''}`}
                onClick={() => setActiveWorkflow(index)}
                onMouseEnter={() => setActiveWorkflow(index)}
              >
                <div className={styles.workflowNumber}>{index + 1}</div>
                <div className={styles.workflowIcon}>
                  {step.icon}
                </div>
                <h3 className={styles.workflowTitle}>{step.title}</h3>
                <p className={styles.workflowDescription}>{step.description}</p>
                <div className={styles.workflowConnector}></div>
              </div>
            ))}
          </div>

          <div className={styles.workflowDetails}>
            <div className={styles.workflowDetailCard}>
              <div className={styles.workflowDetailIcon}>
                {workflowSteps[activeWorkflow].icon}
              </div>
              <h3>{workflowSteps[activeWorkflow].title}</h3>
              <p>{workflowSteps[activeWorkflow].details}</p>
            </div>
          </div>
        </div>
      </section>

      {selectedService && (
        <div className={styles.modalOverlay} onClick={() => setSelectedService(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <button className={styles.closeButton} onClick={() => setSelectedService(null)}>
              <FaTimes />
            </button>
            
            <div className={styles.modalScroll}>
              <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                  <div className={styles.modalIcon}>
                    {selectedService.icon}
                  </div>
                  <h2>{selectedService.title}</h2>
                  <p>{selectedService.description}</p>
                </div>

                <div className={styles.modalGrid}>
                  <div className={styles.modalSection}>
                    <h3>Características</h3>
                    <ul>
                      {selectedService.features.map((feature, index) => (
                        <li key={index}>
                          <FaCheck /> {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className={styles.modalSection}>
                    <h3>Tecnologías</h3>
                    <div className={styles.techGrid}>
                      {selectedService.technologies.map((tech, index) => (
                        <span key={index} className={styles.techBadge}>{tech}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className={styles.modalFooter}>
                  <button className={styles.contactButton}>
                    Contactar ahora
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Services;